"use client";

import React, { useEffect, useState } from "react";
import OfflineBanner from "./OfflineBanner";
import InstallPrompt from "./InstallPrompt";
import PwaModeBadge from "./PwaModeBadge";
import AccountDeletedModal from "./AccountDeletedModal";

type BIPEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

const INSTALL_DISMISS_KEY = "ts_install_prompt_dismissed_at";
const INSTALL_DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000;

const isInstallDismissedRecently = (): boolean => {
  try {
    const dismissedAt = Number(window.localStorage.getItem(INSTALL_DISMISS_KEY) || 0);
    if (!dismissedAt) return false;
    return Date.now() - dismissedAt < INSTALL_DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
};

const rememberInstallDismiss = () => {
  try {
    window.localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now()));
  } catch {}
};

const clearInstallDismiss = () => {
  try {
    window.localStorage.removeItem(INSTALL_DISMISS_KEY);
  } catch {}
};

const SystemOverlays: React.FC = () => {
  const [isOffline, setIsOffline] = useState(false);
  const [installEvt, setInstallEvt] = useState<BIPEvent | null>(null);
  const [installVisible, setInstallVisible] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  // Real online/offline
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const update = () => setIsOffline(!navigator.onLine);
    update();
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  // beforeinstallprompt
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallEvt(e as BIPEvent);
      setInstallVisible(!isInstallDismissedRecently());
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Standalone detection
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(display-mode: standalone)");
    setIsStandalone(mq.matches || (window.navigator as any).standalone === true);
    const fn = (e: MediaQueryListEvent) => setIsStandalone(e.matches);
    mq.addEventListener?.("change", fn);
    return () => mq.removeEventListener?.("change", fn);
  }, []);

  const showOfflineBanner = isOffline;
  const showInstall = installVisible;
  const showPwaBadge = isStandalone;

  const handleInstall = async () => {
    if (installEvt) {
      await installEvt.prompt();
      const { outcome } = await installEvt.userChoice;
      if (outcome === "accepted") clearInstallDismiss();
      setInstallEvt(null);
    }
    setInstallVisible(false);
  };
  const handleDismissInstall = () => {
    rememberInstallDismiss();
    setInstallVisible(false);
  };

  return (
    <>
      {showOfflineBanner && <OfflineBanner />}
      {showInstall && <InstallPrompt onInstall={handleInstall} onDismiss={handleDismissInstall} />}
      {showPwaBadge && <PwaModeBadge />}
      <AccountDeletedModal />
    </>
  );
};

export default SystemOverlays;
